/**
 * Portal Billing hooks (status, checkout, manage subscription).
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { portalFetch, portalKeys } from './portal/shared';

// ============================================================================
// Types
// ============================================================================

export interface PaymentMethodInfo {
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

export interface PortalBillingResponse {
  plan: string;
  has_subscription: boolean;
  stripe_subscription_id: string | null;
  max_channels: number;
  max_users: number;
  max_items_month: number;
  payment_failed: boolean;
  // Dunning state (CUR-431). "past_due" | "canceled" | null (healthy).
  billing_state: string | null;
  grace_period_ends_at: string | null;
  subscription_status: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  // Modern Stripe Customer Portal schedules cancellations via ``cancel_at``
  // (ISO datetime) rather than the legacy boolean. Either signal means the
  // subscription is ending; UI banner checks both (CUR-616).
  cancel_at: string | null;
  payment_method: PaymentMethodInfo | null;
}

export interface CheckoutResponse {
  checkout_url: string;
}

export interface ManageSubscriptionResponse {
  portal_url: string;
}

// ============================================================================
// Hooks
// ============================================================================

export function usePortalBilling() {
  return useQuery({
    queryKey: portalKeys.billing(),
    queryFn: () => portalFetch<PortalBillingResponse>('/portal/billing/status'),
  });
}

export function useCreateCheckout() {
  return useMutation({
    mutationFn: (data: { plan: string; success_url: string; cancel_url: string }) =>
      portalFetch<CheckoutResponse>('/portal/billing/checkout', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  });
}

export function useManageSubscription() {
  return useMutation({
    mutationFn: (data: { return_url: string }) =>
      portalFetch<ManageSubscriptionResponse>('/portal/billing/manage', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  });
}
